/**
 * The rule that `displayCaseSpecs` exists only for an appliance that IS a
 * display case.
 *
 * The exact twin of `winderSafeSpecs.ts`, one kind over, and deliberately built
 * to the same shape rather than merged with it: the two blocks are gated on
 * different flags, carry different fields and are reviewed as separate cells,
 * and a shared generic would have to be parameterised on all three of those to
 * save nothing.
 *
 * Like its sibling, this is an invariant the TYPE cannot express (a type cannot
 * say "this object is null unless that boolean is true"), so it lives on the
 * write path — where every caller passes through — rather than in the form,
 * which a bulk enrichment apply bypasses entirely.
 */
import type { WinderEntry } from '../types';

export type WinderDisplayCaseSpecs = NonNullable<WinderEntry['displayCaseSpecs']>;

/**
 * The seven fields the block can carry, in the order the editor shows them.
 *
 * Glazing leads because it is the one an owner can see, and the two humidity
 * fields sit together because they are constantly confused: a hygrometer
 * MEASURES, humidity control REGULATES, and marketing copy uses either word for
 * both.
 */
export const DISPLAY_CASE_SPEC_KEYS = [
  'glazing',
  'hasUvFiltering',
  'hasIllumination',
  'hasHygrometer',
  'hasHumidityControl',
  'mountingStyle',
  'hasDrawer',
] as const;

/** The glazing materials the type allows, as a runtime list for the editor. */
export const GLAZING_MATERIALS = [
  'glass',
  'tempered_glass',
  'acrylic',
  'crystal',
] as const;

/** Human labels for the select. */
export const GLAZING_LABELS: Record<string, string> = {
  glass: 'Glass',
  tempered_glass: 'Tempered glass',
  acrylic: 'Acrylic',
  crystal: 'Crystal',
};

/** The mounting styles the type allows. */
export const MOUNTING_STYLES = [
  'tabletop',
  'wall_mounted',
  'freestanding',
  'drawer_insert',
] as const;

export const MOUNTING_STYLE_LABELS: Record<string, string> = {
  tabletop: 'Tabletop',
  wall_mounted: 'Wall mounted',
  freestanding: 'Freestanding',
  drawer_insert: 'Drawer insert',
};

/**
 * What `saveWinder` writes to `displayCaseSpecs`.
 *
 * Returns null — clearing the block — in two cases, and they are different
 * facts that happen to share an outcome:
 *
 *  - the appliance is not a display case, so the block cannot apply. Unticking
 *    "is a display case" and saving MUST clear it; leaving orphaned glazing on
 *    an ordinary winder would have the RN app hide it (it gates on
 *    `hasDisplayCase`) while the document still carried it, which is the worst
 *    of both.
 *  - every field is null, so the block says nothing. An empty map is
 *    indistinguishable to a reader from "we checked and found nothing".
 *
 * ⚠️ GATED ON `hasDisplayCase`, NOT ON `applianceKind === 'display_case'`, and
 * that is the whole reason a glass-fronted WINDER keeps its glazing. Same rule
 * `resolveSafeSpecsWrite` follows for a winder built into a safe.
 */
export function resolveDisplayCaseSpecsWrite(
  hasDisplayCase: boolean,
  input?: WinderEntry['displayCaseSpecs'],
): WinderDisplayCaseSpecs | null {
  if (!hasDisplayCase || !input) return null;

  const out: Record<string, unknown> = {};
  for (const key of DISPLAY_CASE_SPEC_KEYS) {
    const value = (input as Record<string, unknown>)[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[key] = trimmed;
      continue;
    }
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }

  return Object.keys(out).length > 0 ? (out as WinderDisplayCaseSpecs) : null;
}
